<template>
  <div>
    <DataTableWrapper
      ref="tableWrapper"
      :initialSortBy="initialSortBy"
      :data-source="{
        type: 'AxisGQL',
        query: require('@/services/gql/types/HistoricalFigures/queries/historicalFiguresGrid.gql')
      }"
      :headers="$options.headersConfig"
      :filters="{
        name: { control: 'text', modifier: 'contains' },
        slug: { control: 'text', modifier: 'contains' },
        status: {
          control: 'select',
          options: [...$options.statusList],
          dataType: String
        },
        createdAt: { control: 'date-range' },
        updatedAt: { control: 'date-range' },
        isActive: {
          control: 'select',
          options: [
            {
              value: true,
              text: 'Да'
            },
            {
              value: false,
              text: 'Нет'
            }
          ],
          dataType: Boolean
        },
        translations: {
          control: 'select',
          options: [
            {
              value: 'ru',
              text: 'ru'
            },
            {
              value: 'en',
              text: 'en'
            },
            {
              value: 'ja',
              text: 'ja'
            },
            {
              value: 'zh',
              text: 'zh'
            },
            {
              value: 'ko',
              text: 'ko'
            }
          ]
        }
      }"
      :stored-headers="getCurrentViewCols"
      :views-list="getViewList"
      :active-view-key="getActiveViewKey"
      :views-loading="$async.updateMe.$pending"
      @query-state-updated="getStateQuery"
      @view-changed="updateStoredView"
      @active-view-changed="updateStoredActiveView"
      @remove-view="removeStoredView"
      @data-loaded="onTableDataChange"
    >
      <template v-slot="{ tableProps, tableListeners, filtersProps, filtersListeners }">
        <v-data-table
          fixed-header
          height="80vh"
          :items-per-page="$options.perPage.itemsPerPage"
          :footer-props="{ itemsPerPageOptions: $options.perPage.itemsPerPageOptions }"
          v-bind="tableProps"
          v-on="tableListeners"
          v-model="mixSelectedItems"
          @update:sort-by="updateSortBy"
          @update:sort-desc="sortDesc = $event"
        >
          <template v-for="(h, i) in $options.headersConfig" v-slot:[`header.${h.value}`]>
            <v-tooltip open-delay="350" :key="i" bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ h.text }}</span>
              </template>
              <span>{{ h.description }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`body.prepend`]="{}">
            <DataTableFilters
              is-grid-selectable
              v-bind="filtersProps"
              v-on="filtersListeners"
            />
          </template>
          <template
            v-for="(h, i) in $options.headersConfig"
            v-slot:[`item.${h.value}`]="{ item }"
          >
            <div :key="i">
              <div v-if="h.type === 'number' && item[h.value] !== null">
                {{ formatAmount(item[h.value]) }}
              </div>
              <div v-else-if="h.text === 'Имя'">
                <router-link
                  title="Редактировать"
                  :to="{
                    name: 'page-historical-figure',
                    params: { id: item.id.toString() }
                  }"
                  >{{ item[h.value] }}</router-link
                >
              </div>
              <div v-else-if="h.type === 'date'">
                <DateTimeCell :value="item[h.value]" />
              </div>
              <div v-else-if="h.value === 'slug'">
                <div>{{ mixCutSlug(item[h.value]) }}</div>
              </div>
              <div v-else-if="h.value === 'copySlug'">
                <a title="Копировать">
                  <v-btn icon @click="mixCopySlugHandler(item)">
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </a>
              </div>
              <div v-else-if="h.type === 'boolean'">
                <v-icon
                  v-text="'mdi-checkbox-marked-circle'"
                  :color="item[h.value] ? 'success' : 'grey'"
                />
              </div>
              <div v-else-if="h.type === 'actions'">
                <router-link
                  :to="{
                    name: 'page-historical-figure',
                    params: { id: item.id.toString() }
                  }"
                  title="Редактировать"
                  ><v-btn icon><v-icon>mdi-pencil</v-icon></v-btn></router-link
                >
                <a title="Удалить">
                  <v-btn icon @click="deleteHistoricalFigure(item.id)">
                    <v-icon color="red">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </a>
              </div>
              <div v-else-if="h.value === 'translations'">{{ getLanguage(item) }}</div>
              <div v-else-if="h.value === 'status'">{{ mixGetStatus(item) }}</div>
              <div v-else>
                {{ get(item, h.value) }}
              </div>
            </div>
          </template>
        </v-data-table>
      </template>
    </DataTableWrapper>
    <ConfirmationDialog ref="confirmDeleteHistoricalFigure" />
  </div>
</template>

<script>
import DataTableWrapper from '@/components/data-table-wrapper'
import DataTableFilters from '@/components/data-table-filters'
import ConfirmationDialog from '@/components/confirmation-dialog'
import { formatAmount, get } from '@/utils'
import { paramsToListQueryInput } from '@/components/data-table-wrapper/data-sources/axis-gql/utils'
import perPage from '@/configs/v-data-table.js'
import headersConfig from './headersConfig'
import deleteHistoricalFigure from '@/services/gql/types/HistoricalFigures/mutations/deleteHistoricalFigure.gql'
import { gridDependentMixin } from '@/pages/grid-dependent.mix'
import updateMeMutation from '@/services/gql/types/Me/mutations/updateMe.gql'
import DateTimeCell from '@/components/date-time-cell'

export default {
  perPage,
  headersConfig,
  name: 'GridHistoricalFigures',
  gridType: 'historialFigures',
  mixins: [gridDependentMixin],
  components: {
    DataTableWrapper,
    DataTableFilters,
    ConfirmationDialog,
    DateTimeCell
  },
  props: {
    items: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      queryState: null,
      sortDesc: false,
      initialSortBy: []
    }
  },
  methods: {
    get,
    refetch () {
      this.$refs.tableWrapper.refresh()
    },
    formatAmount,
    getStateQuery (state) {
      this.queryState = paramsToListQueryInput(state)
    },
    async deleteHistoricalFigure (id) {
      try {
        await this.$refs.confirmDeleteHistoricalFigure.open()
        await this.$async.deleteHistoricalFigure.$perform({ id })
        this.$successMsg('Историческая личность удалена')
        this.refetch()
      } catch (e) {
        if (e.cancel) return
        this.$handleApiError(
          e.graphQLErrors,
          `Ошибка при удалении исторической личности с ${id}`
        )
      }
    },
    updateSortBy ([field]) {
      this.$set(this.initialSortBy, 0, {
        field,
        dir: this.sortDesc[0] ? 'desc' : 'asc'
      })
      this.refetch()
    }
  },
  async: {
    deleteHistoricalFigure (variables) {
      return this.$apollo.mutate({
        mutation: deleteHistoricalFigure,
        variables
      })
    },
    updateMe (variables) {
      return this.$apollo.mutate({
        mutation: updateMeMutation,
        variables
      })
    }
  }
}
</script>
